/**
 * Settlement economy vocabulary.
 *
 * Buildings describe space and throughput. Workers describe knowledge and
 * quality. Keeping these catalogs separate is the central invariant of the
 * settlement loop; a larger building never teaches a resident a recipe.
 */
import type { RoomFunction } from "../homestead/rooms.js";
import type { ItemQuality } from "../rpg.js";

export const SETTLEMENT_BUILDING_FAMILIES = [
  "generator", "refiner", "crafter", "trader", "admin", "living",
] as const;
export type SettlementBuildingFamily = (typeof SETTLEMENT_BUILDING_FAMILIES)[number];

export const SETTLEMENT_BUILDING_VARIANTS = [
  "mine", "farm", "salvage-yard", "harvester",
  "foundry", "bakery", "processor", "fuel-refinery",
  "smithy", "workshop", "garage", "outfit-bench",
  "market-stall", "exchange", "supply-counter",
  "office", "house", "dormitory", "guest-house",
  "combiner", "shipyard", "bank", "guild-hall",
] as const;
export type SettlementBuildingVariant = (typeof SETTLEMENT_BUILDING_VARIANTS)[number];
export type SettlementBuildingLevel = 1 | 2 | 3 | 4 | 5;

export interface SettlementBuildingCapacity {
  inputSlots: number;
  outputSlots: number;
  workerSlots: number;
  parallelJobs: number;
  /** Number of target/routing rules the building can own. */
  ruleSlots: number;
  /** Living only. Real beds remain the lower authoritative cap. */
  residentSlots: number;
  visitorSlots: number;
}

const cap = (inputSlots: number, outputSlots: number, workerSlots: number,
  parallelJobs: number, ruleSlots = 0, residentSlots = 0,
  visitorSlots = 0): SettlementBuildingCapacity => ({
  inputSlots, outputSlots, workerSlots, parallelJobs, ruleSlots, residentSlots, visitorSlots,
});

/** Contextual capacities from the design. These are gameplay data, not a
 * serialized contract, so tuning them does not invalidate a save. */
export const SETTLEMENT_BUILDING_CAPACITIES: Readonly<Record<SettlementBuildingFamily,
  Readonly<Record<SettlementBuildingLevel, SettlementBuildingCapacity>>>> = {
  generator: {
    1: cap(2, 4, 1, 1), 2: cap(4, 10, 2, 1), 3: cap(8, 20, 3, 2),
    4: cap(12, 40, 4, 3), 5: cap(20, 80, 6, 4),
  },
  refiner: {
    1: cap(4, 4, 1, 1), 2: cap(8, 8, 2, 1), 3: cap(16, 16, 3, 2),
    4: cap(32, 32, 4, 3), 5: cap(64, 64, 6, 4),
  },
  crafter: {
    1: cap(6, 2, 1, 1), 2: cap(12, 4, 2, 1), 3: cap(24, 8, 3, 2),
    4: cap(48, 16, 4, 3), 5: cap(96, 32, 6, 4),
  },
  trader: {
    1: cap(8, 4, 1, 2, 2), 2: cap(16, 8, 2, 4, 4), 3: cap(32, 16, 3, 8, 8),
    4: cap(64, 32, 4, 16, 16), 5: cap(128, 64, 6, 32, 32),
  },
  admin: {
    1: cap(3, 0, 1, 1, 2), 2: cap(6, 0, 2, 1, 5), 3: cap(12, 0, 3, 1, 10),
    4: cap(24, 0, 4, 1, 20), 5: cap(1_000_000, 0, 6, 1, 40),
  },
  living: {
    1: cap(0, 0, 0, 0, 0, 2), 2: cap(0, 0, 0, 0, 0, 4, 1),
    3: cap(0, 0, 0, 0, 0, 8, 2), 4: cap(0, 0, 0, 0, 0, 12, 4),
    5: cap(0, 0, 0, 0, 0, 20, 6),
  },
};

export interface SettlementBuildingVariantDef {
  family: SettlementBuildingFamily;
  name: string;
  roomFunctions: readonly RoomFunction[];
  professions: readonly string[];
}

export const SETTLEMENT_BUILDING_DEFS: Readonly<Record<SettlementBuildingVariant,
  SettlementBuildingVariantDef>> = {
  mine: { family: "generator", name: "Mine", roomFunctions: ["mine"], professions: ["miner"] },
  farm: { family: "generator", name: "Biogenerator", roomFunctions: ["farm"], professions: ["farmer"] },
  "salvage-yard": { family: "generator", name: "Reclaimer", roomFunctions: ["salvage"], professions: ["salvager"] },
  // V2 economy (2026-08-13): the harvester wire id is REPURPOSED as the Materia
  // Synthesizer. The old "harvester" profession legacy-maps to Synthesist.
  harvester: { family: "generator", name: "Materia Synthesizer", roomFunctions: ["harvester"], professions: ["synthesist"] },
  foundry: { family: "refiner", name: "Foundry", roomFunctions: ["workshop"], professions: ["metallurgist"] },
  // V2 economy (2026-08-14): the bakery wire id is the KITCHEN; "baker"
  // legacy-maps to Chef. The Combiner performs both secondary-Materia
  // conversion and metal+Materia ingot infusion in one refinery (§4.3).
  bakery: { family: "refiner", name: "Kitchen", roomFunctions: ["kitchen"], professions: ["chef"] },
  processor: { family: "refiner", name: "Processor", roomFunctions: ["kitchen", "workshop"], professions: ["chemist"] },
  combiner: { family: "refiner", name: "Materia Combiner", roomFunctions: ["workshop"], professions: ["combiner"] },
  "fuel-refinery": { family: "refiner", name: "Fuel Refinery", roomFunctions: ["workshop"], professions: ["fuel-technician"] },
  smithy: { family: "crafter", name: "Smithy", roomFunctions: ["smithy"], professions: ["weaponsmith"] },
  workshop: { family: "crafter", name: "Workshop", roomFunctions: ["workshop"], professions: ["fabricator"] },
  // V2 economy (2026-08-14): the "garage" wire id is the ENGINEER's works;
  // the Shipwright's vehicle Garage is the new "shipyard" variant. The
  // "outfit-bench" wire id is the merged Outfitter ("armorer" legacy-maps).
  garage: { family: "crafter", name: "Engineering Works", roomFunctions: ["workshop"], professions: ["engineer"] },
  shipyard: { family: "crafter", name: "Garage", roomFunctions: ["workshop"], professions: ["shipwright"] },
  "outfit-bench": { family: "crafter", name: "Outfitting Post", roomFunctions: ["workshop"], professions: ["outfitter"] },
  "market-stall": { family: "trader", name: "Market Stall", roomFunctions: ["exchange"], professions: ["merchant"] },
  exchange: { family: "trader", name: "Exchange", roomFunctions: ["exchange"], professions: ["broker"] },
  // Civic economy (§6): the Bank is UNSTAFFED at launch (locked decision);
  // the Guild Hall's Liaison negotiates orders rather than running recipes.
  bank: { family: "trader", name: "Bank", roomFunctions: ["exchange"], professions: [] },
  "guild-hall": { family: "trader", name: "Guild Hall", roomFunctions: ["exchange"], professions: ["liaison"] },
  "supply-counter": { family: "trader", name: "Supply Counter", roomFunctions: ["exchange"], professions: ["quartermaster"] },
  office: { family: "admin", name: "Administration", roomFunctions: ["office"], professions: ["steward", "foreman", "coordinator", "recruiter"] },
  house: { family: "living", name: "House", roomFunctions: ["dwelling"], professions: [] },
  dormitory: { family: "living", name: "Dormitory", roomFunctions: ["dwelling"], professions: [] },
  "guest-house": { family: "living", name: "Guest House", roomFunctions: ["dwelling"], professions: [] },
};

export function settlementBuildingCapacity(family: SettlementBuildingFamily,
  level: SettlementBuildingLevel): SettlementBuildingCapacity {
  return SETTLEMENT_BUILDING_CAPACITIES[family][level];
}

export function settlementVariantAcceptsProfession(variant: SettlementBuildingVariant,
  profession: string | null): boolean {
  const specialty = legacyProfessionSpecialty(profession);
  return specialty !== null && SETTLEMENT_BUILDING_DEFS[variant].professions.includes(specialty);
}

export const SETTLEMENT_QUALITY_TIERS = ["low", "medium", "high", "premium", "pristine"] as const;
export type SettlementQuality = (typeof SETTLEMENT_QUALITY_TIERS)[number];
export type SettlementQualityCounts = [number, number, number, number, number];

export const SETTLEMENT_QUALITY_INDEX: Readonly<Record<SettlementQuality, number>> = {
  low: 0, medium: 1, high: 2, premium: 3, pristine: 4,
};

/** Player-stack grade ⇄ settlement tier. An unstamped stack is Medium (Q2) —
 * the same legacy default the storage migration uses — so ordinary goods
 * round-trip the settlement boundary without ever acquiring a stamp. */
export function itemQualityToTier(q: ItemQuality | undefined): SettlementQuality {
  return q === undefined ? "medium" : SETTLEMENT_QUALITY_TIERS[q - 1]!;
}

export function tierToItemQuality(tier: SettlementQuality): ItemQuality | undefined {
  return tier === "medium" ? undefined : (SETTLEMENT_QUALITY_INDEX[tier] + 1) as ItemQuality;
}

export interface SettlementQualityStack {
  id: string;
  n: number;
  quality: SettlementQuality;
}

export type SettlementQualityPolicy = "lowest-first" | "at-least-high" | "exact";

/** Nearest semantic equivalent for pre-six-family and pre-V2-economy saves. */
export function legacyProfessionSpecialty(profession: string | null): string | null {
  switch (profession) {
    case "refiner": return "metallurgist";
    case "provisioner": return "chemist";
    case "researcher": return "steward";
    case "harvester": return "synthesist";
    case "baker": return "chef";
    case "armorer": return "outfitter";
    default: return profession;
  }
}

export function inferredBuildingVariant(roomFunction: RoomFunction,
  profession: string | null): SettlementBuildingVariant {
  const specialty = legacyProfessionSpecialty(profession);
  if (roomFunction === "mine") return "mine";
  if (roomFunction === "farm") return "farm";
  if (roomFunction === "salvage") return "salvage-yard";
  if (roomFunction === "harvester") return "harvester";
  if (roomFunction === "smithy") return "smithy";
  if (roomFunction === "kitchen") return specialty === "chemist" ? "processor" : "bakery";
  if (roomFunction === "exchange") {
    return specialty === "broker" ? "exchange" : specialty === "liaison" ? "guild-hall" : "market-stall";
  }
  if (roomFunction === "office") return "office";
  if (roomFunction === "dwelling") return "house";
  if (specialty === "metallurgist") return "foundry";
  if (specialty === "engineer") return "garage";
  if (specialty === "combiner") return "combiner";
  if (specialty === "shipwright") return "shipyard";
  if (specialty === "outfitter") return "outfit-bench";
  return "workshop";
}
